import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import useInfiniteScroll from 'hooks/useInfiniteScroll/useInfiniteScroll';
import { usePathname } from 'hooks/usePathname/usePathname';
import { TWhatsNewNotification } from 'lendingApi/whatsNew';
import { IwQueryClient } from 'store/IwQueryClient';
import { getPathname } from 'utils/common/common';
import { PATH_NAMES } from 'utils/routes/pathNames';
import {
  getWhatsNewContent,
  markAllWhatsNewContentAsRead,
} from './useWhatsNew.api';

export const useWhatsNew = (
  containerRef: React.RefObject<HTMLDivElement>,
  introducerId: string,
  fetchDataOnly = false,
) => {
  const [hasMarkedRead, setHasMarkedRead] = useState(false);
  const pathname = usePathname();

  const {
    data,
    hasNextPage,
    isLoading,
    isLoadingMore,
    isError,
    error,
    isEmpty,
  } = useInfiniteScroll<TWhatsNewNotification>({
    queryKey: ['whatsNewContent'],
    apiFunction: (page) => getWhatsNewContent(introducerId as string, page),
    containerRef,
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: () => markAllWhatsNewContentAsRead(introducerId),
    onSuccess: () => {
      IwQueryClient.getOrCreate().invalidateQueries({
        queryKey: ['whatsNewContent'],
      });
    },
  });

  const numberUnread = (data || []).filter((item) => !item.is_read).length;

  useEffect(() => {
    if (
      !fetchDataOnly &&
      numberUnread > 0 &&
      !hasMarkedRead &&
      pathname === getPathname(PATH_NAMES.whatsNew())
    ) {
      markAllAsReadMutation.mutate();
      setHasMarkedRead(true); // To prevent infinite requests if the operation fails
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numberUnread, hasMarkedRead, pathname]);

  return {
    data: data || [],
    isLoading,
    isLoadingMore,
    error,
    isError,
    hasMoreContent: hasNextPage,
    numberUnread,
    hasData: !isEmpty && !isError && data !== null,
    isEmpty,
  };
};
